import { clientDayKey, clientTime, dateTimeNoSeconds, shortDay } from '../../shared/dateTime';

/**
 * Prezentacja stanu publikacji giełdowej na liście zleceń.
 *
 * **Źródłem prawdy dla zestawu statusów jest backend**:
 * `apps/api/src/postings/posting-statuses.ts`. Tutaj są wyłącznie kolory
 * i zdania — etykiety przychodzą gotowe z API (pole `exchanges[].statusLabel`),
 * bo to backend decyduje, co w ogóle trafia do kolumny.
 *
 * Od 07.10.2026 (prezentacja 02.10, pkt 9) API oddaje też `since` (od kiedy
 * w stanie) i `error` (powód nieudanej próby) — kolumna „Giełda” pokazuje
 * „od kiedy” pod chipem, a powód w dymku.
 *
 * ## Dlaczego kolor nie niesie znaczenia sam
 *
 * Stan jest zawsze napisany słowem, a kolor tylko to powtarza. Operator
 * odróżniający kolory słabo albo patrzący na wydruk czyta to samo, co reszta.
 *
 * Od 08.10.2026 (decyzja użytkownika: kolumna „Giełda” zostaje i ma się
 * zmieścić przy 1366 px) słowo stoi pod chipem, nie w nim: chip to giełda
 * z ikoną stanu („TIMOCOM” ✓), pod nim „wystawiona od 09:27”
 * (`publicationCaption`), a pełne zdanie z datą — w dymku. „TIMOCOM:
 * Wystawiona” w jednym chipie rozpychało kolumnę do ~175 px; pełna etykieta
 * zostaje nazwą chipa dla czytnika ekranu i w eksporcie (`publicationLabel`).
 */

export const STATUS_PENDING = 'pending';
export const STATUS_POSTED = 'posted';
export const STATUS_FAILED = 'failed';

/** Kolory MUI. Nieznany stan nie dostaje koloru „w porządku". */
export const STATUS_COLORS: Record<string, 'default' | 'success' | 'error' | 'warning'> = {
  [STATUS_PENDING]: 'warning',
  [STATUS_POSTED]: 'success',
  [STATUS_FAILED]: 'error',
};

export function publicationColor(status: unknown): 'default' | 'success' | 'error' | 'warning' {
  return typeof status === 'string' && status in STATUS_COLORS ? STATUS_COLORS[status] : 'default';
}

/** Stan giełdowy, jak go oddaje API (`user-offers/exchange-status-column.ts`). */
export type ExchangeState = {
  exchange: string;
  exchangeLabel: string;
  status: string;
  statusLabel: string;
  link: string | null;
  /** Od kiedy w stanie (ISO); `null` = nie wiemy. */
  since: string | null;
  /** Powód nieudanej próby (tylko `failed`); `null` = nieznany. */
  error: string | null;
};

const asText = (w: unknown): string => (typeof w === 'string' ? w : '');

/**
 * Stany giełdowe z rekordu listy.
 *
 * Wiersz bez pola (starsza odpowiedź API, podgląd z innego źródła) daje pustą
 * listę — kolumna pokaże myślnik zamiast udawać, że zlecenia nie ma na
 * giełdzie. To dwie różne rzeczy, ale w kolumnie wyglądają tak samo, więc
 * różnicę niesie tekst pomocniczy w nagłówku, nie kolor.
 */
export function exchangeStates(record: Record<string, unknown>): ExchangeState[] {
  const rawList = record.exchanges;
  if (!Array.isArray(rawList)) return [];
  return rawList
    .filter((s): s is Record<string, unknown> => !!s && typeof s === 'object')
    .map((s) => ({
      exchange: asText(s.exchange),
      exchangeLabel: asText(s.exchangeLabel) || asText(s.exchange),
      status: asText(s.status),
      statusLabel: asText(s.statusLabel),
      link: typeof s.link === 'string' && s.link !== '' ? s.link : null,
      since: typeof s.since === 'string' && s.since !== '' ? s.since : null,
      error: typeof s.error === 'string' && s.error.trim() !== '' ? s.error.trim() : null,
    }))
    .filter((s) => s.exchange !== '');
}

/** Napis na chipie: giełda i stan razem, nigdy sam kolor. */
export function publicationLabel(s: ExchangeState): string {
  return s.statusLabel ? `${s.exchangeLabel}: ${s.statusLabel}` : s.exchangeLabel;
}

/**
 * Chwila stanu krótko: dziś sama godzina („09:27”), wcześniej sam dzień
 * („05.10”) — pełna data z godziną jest w dymku. Brak albo nieczytelny
 * znacznik = `null` (nie „teraz”).
 */
export function publicationWhen(since: string | null, now: Date = new Date()): string | null {
  if (!since) return null;
  const d = new Date(since);
  if (Number.isNaN(d.getTime())) return null;
  return clientDayKey(d) === clientDayKey(now) ? clientTime(since) : shortDay(since);
}

/**
 * Podpis pod chipem: stan słowem i od kiedy — „wystawiona od 09:27”,
 * „oczekująca od 05.10”. Dla nieudanej to chwila porażki, więc bez „od”
 * („nieudana 05.10”). Bez znacznika — sam stan, nic nie zgadujemy.
 * Etykieta stanu przychodzi z API; tu tylko mała litera na początku.
 */
export function publicationCaption(s: ExchangeState, now: Date = new Date()): string {
  const label = s.statusLabel || s.status;
  const word = label ? label.charAt(0).toLocaleLowerCase('pl-PL') + label.slice(1) : '';
  const when = publicationWhen(s.since, now);
  if (!when) return word;
  return s.status === STATUS_FAILED ? `${word} ${when}` : `${word} od ${when}`;
}

/**
 * Pełne zdanie do dymka: co, na której giełdzie, od kiedy — a przy porażce
 * powód z API (to samo zdanie, które widać w karcie zlecenia). Powodu nie
 * znamy = odsyłamy do karty, nie wymyślamy.
 */
export function publicationHint(s: ExchangeState): string {
  const at = s.since ? dateTimeNoSeconds(s.since) : '—';
  const when = at === '—' ? '' : at;
  switch (s.status) {
    case STATUS_POSTED:
      return [
        `Wystawione na ${s.exchangeLabel}${when ? ` od ${when}` : ''}.`,
        s.link ? 'Kliknij, aby otworzyć ofertę na giełdzie.' : '',
      ]
        .filter(Boolean)
        .join(' ');
    case STATUS_PENDING:
      return `Czeka w kolejce do wystawienia na ${s.exchangeLabel}${when ? ` od ${when}` : ''}.`;
    case STATUS_FAILED:
      return [
        `Ostatnia próba wystawienia na ${s.exchangeLabel} nie powiodła się${when ? ` (${when})` : ''}.`,
        s.error ? `Powód: ${s.error}` : 'Powód — na karcie zlecenia.',
      ].join(' ');
    default:
      return publicationLabel(s);
  }
}
